import type { CurrencyCode } from '@faststore/sdk'
import { useSession } from '@faststore/sdk'
import { useEffect, useMemo, useRef } from 'react'
import { useStoreContext } from 'src/contexts/store-context'
import { trackDYSyncCart } from 'src/sdk/analytics/dynamicYield'
import { useCart } from 'src/sdk/cart/useCart'
import { PageTypes } from 'src/typings/pageTypes'
import { getDynamicYieldContext } from 'src/utils/getDynamicYieldContext'
import { DYNAMIC_YIELD_SCRIPT_READY_EVENT } from './DynamicYieldScriptLoader'

export const DYNAMIC_YIELD_CONTEXT_READY_EVENT = 'dynamic-yield:context-ready'

type Props = {
  pathname?: string
  productSku?: string | null
  categoryPath?: string[]
  pageType?: PageTypes
}

const DynamicYieldPageContext = ({
  pathname = '/',
  productSku = null,
  categoryPath = [],
  pageType = PageTypes.NotSet,
}: Props) => {
  const { items } = useCart()
  const {
    currency: { code },
    isValidating,
  } = useSession()
  const lastSyncedCart = useRef<string | null>(null)
  const { favouriteStore } = useStoreContext()
  const cartSkus = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map((item) => item?.itemOffered?.sku)
            .filter((sku): sku is string => Boolean(sku))
        )
      ),
    [items]
  )
  const branchId = useMemo(
    () => favouriteStore?.id?.trim() || null,
    [favouriteStore?.id]
  )

  // Componente senza UI: viene montato nei layout Gatsby dal contenitore browser.
  // Scrive il contesto pagina consumato dagli script DY dopo il primo montaggio
  // lato client.
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Gira sul client per ogni contenitore pagina Gatsby renderizzato. Aggiorna
    // il contesto usato da DY per scegliere le campagne, ma non scarica campagne
    // da solo: sono gli script DY gia' caricati a consumare questo oggetto
    // globale.
    const safePathname = window.location?.pathname ?? pathname

    const recommendationContext = getDynamicYieldContext({
      pathname: safePathname,
      productSku,
      cartSkus,
      categoryPath,
      pageType,
      branchId,
    })

    // Gli script pubblici DY leggono questo globale. Tenerlo aggiornato e'
    // importante nei cambi rotta lato client, dove Gatsby non ricarica il
    // documento.
    window.DY = window.DY ?? {}
    window.DY.recommendationContext = recommendationContext
    window.dispatchEvent(new CustomEvent(DYNAMIC_YIELD_CONTEXT_READY_EVENT))
  }, [pathname, productSku, cartSkus, categoryPath, pageType, branchId])

  // Sync cart: DY needs it on every page load (Exclude items in cart,
  // Reconnect) and whenever the cart changes. The ref lives per page
  // container, so each page load sends it once, while re-renders with the same
  // cart content don't.
  useEffect(() => {
    if (typeof window === 'undefined' || isValidating) return

    const syncCart = () => {
      const key = JSON.stringify(
        items.map(({ itemOffered, quantity, price }) => [
          itemOffered?.sku,
          quantity,
          price,
        ])
      )

      if (lastSyncedCart.current === key) return

      // Dropped without consent or DY script: retried on the next signal below
      if (trackDYSyncCart(items, code as CurrencyCode)) {
        lastSyncedCart.current = key
      }
    }

    syncCart()

    window.addEventListener(DYNAMIC_YIELD_SCRIPT_READY_EVENT, syncCart)
    window.addEventListener('CookiebotOnConsentReady', syncCart)
    window.addEventListener('CookiebotOnAccept', syncCart)

    return () => {
      window.removeEventListener(DYNAMIC_YIELD_SCRIPT_READY_EVENT, syncCart)
      window.removeEventListener('CookiebotOnConsentReady', syncCart)
      window.removeEventListener('CookiebotOnAccept', syncCart)
    }
  }, [items, code, isValidating])

  return null
}

export default DynamicYieldPageContext
